/**
 * Kanban column sort — compare keys and completeness scoring
 */

import { CommissionScheduleService } from '../services/CommissionScheduleService.js';

const RATING_RANK = { hot: 3, warm: 2, cold: 1 };

export const KANBAN_SORT_OPTIONS = [
    { key: 'updatedAt', label: 'Last modified', defaultDir: 'desc' },
    { key: 'createdAt', label: 'Created date', defaultDir: 'desc' },
    { key: 'dealAmount', label: 'Deal amount', defaultDir: 'desc' },
    { key: 'forecastAmount', label: 'Forecast value', defaultDir: 'desc' },
    { key: 'completeness', label: 'Info completeness', defaultDir: 'desc' },
    { key: 'commissionPct', label: 'Total commission %', defaultDir: 'desc' },
    { key: 'commissionAmount', label: 'Commission amount', defaultDir: 'desc' },
    { key: 'accountRating', label: 'Account heat (hot → cold)', defaultDir: 'desc' },
    { key: 'expectedCloseDate', label: 'Expected close date', defaultDir: 'asc' },
    { key: 'probability', label: 'Win probability', defaultDir: 'desc' },
    { key: 'hasNextStep', label: 'Next step set', defaultDir: 'desc' },
    { key: 'dealName', label: 'Deal name (A–Z)', defaultDir: 'asc' }
];

export function computeDealCompleteness(deal) {
    const checks = [
        !!(deal.dealName || deal.name),
        !!deal.clientId,
        !!deal.ownerUserId,
        (deal.dealAmount || deal.amount) > 0,
        deal.probability != null && deal.probability !== '',
        !!(deal.nextStep || '').trim(),
        !!deal.expectedCloseDate,
        !!deal.primaryContactId,
        !!deal.dealType,
        !!deal.leadSource,
        !!deal.dealPipeline,
        !!deal.serviceLine
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

export function getDealCommissionPct(deal) {
    if (!deal.commissionEligible) return 0;
    const splits = deal.commissionSplits || [];
    if (splits.length) {
        return splits.reduce((s, r) => s + (Number(r.splitPct) || 0), 0);
    }
    return Number(deal.commissionRate) || 0;
}

export function getDealCommissionAmount(deal) {
    return CommissionScheduleService.estimateTotalCommission(deal);
}

export function buildKanbanSortValues(deal) {
    return {
        updatedAt: deal.updatedAt || '',
        createdAt: deal.createdAt || '',
        dealAmount: Number(deal.dealAmount || deal.amount) || 0,
        forecastAmount: Number(deal.forecastAmount) || 0,
        completeness: deal.completenessScore ?? computeDealCompleteness(deal),
        commissionPct: deal.commissionPct ?? getDealCommissionPct(deal),
        commissionAmount: deal.commissionAmount ?? getDealCommissionAmount(deal),
        accountRating: RATING_RANK[deal.accountRating] ?? 0,
        expectedCloseDate: deal.expectedCloseDate || '9999-12-31',
        probability: Number(deal.probability) || 0,
        hasNextStep: (deal.nextStep || '').trim() ? 1 : 0,
        dealName: (deal.dealName || deal.name || '').toLowerCase()
    };
}

function compareValues(a, b, key, dir) {
    const mult = dir === 'asc' ? 1 : -1;
    let va = a._kanbanSort?.[key];
    let vb = b._kanbanSort?.[key];

    if (key === 'dealName') {
        return mult * String(va).localeCompare(String(vb));
    }
    if (key === 'updatedAt' || key === 'createdAt' || key === 'expectedCloseDate') {
        return mult * String(va).localeCompare(String(vb));
    }
    va = Number(va) || 0;
    vb = Number(vb) || 0;
    if (va === vb) {
        return String(a.dealName || '').localeCompare(String(b.dealName || ''));
    }
    return mult * (va - vb);
}

export function sortKanbanDeals(deals, sortKey, dir) {
    const enriched = deals.map(d => ({
        ...d,
        _kanbanSort: d._kanbanSort || buildKanbanSortValues(d)
    }));
    return enriched.sort((a, b) => compareValues(a, b, sortKey, dir));
}
